import React, { useEffect, useState, useRef } from 'react';
import RecipeCard from '../components/RecipeCard';
import Masonry from 'react-masonry-css';
import { useCSRF } from '../utils/csrfUtils';
import { Button, Spinner } from 'react-bootstrap';
import ErrorBoundary from '../components/ErrorBoundary';
import { useAuth } from '../context/AuthContext';
import { useGetAllFavoriteRecipes } from '../hooks/favoriteRecipe';
import { useGetAllDislikedRecipes } from '../hooks/dislikeRecipe';
import supabase from '../supabaseClient';

function RecipeList({ searchTerm = '', onMealAdded }) {
  const { pgUser, sessionToken } = useAuth();
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [recipes, setRecipes] = useState([]);
  const [cuisines, setCuisines] = useState([]);
  const [selectedCuisines, setSelectedCuisines] = useState([]);
  const [isFetching, setIsFetching] = useState(false); // More subtle than "loading"
  const { fetchWithCSRF, isLoading: csrfLoading } = useCSRF();
  const filtersRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { getAllFavoriteRecipes, isLoading: isLoadingFavorites, error: errorFavorites } = useGetAllFavoriteRecipes();
  const { getAllDislikedRecipes } = useGetAllDislikedRecipes();
  const [favoriteRecipes, setFavoriteRecipes] = useState([]);
  const [dislikedRecipes, setDislikedRecipes] = useState([]);
  const [pantryIngredients, setPantryIngredients] = useState(null);

  useEffect(() => {
    const fetchFavoritesAndDislikes = async () => {
      if (!sessionToken) return;
      
      try {
        // Fetch favorites
        console.log('about to call getAllFavoriteRecipes');
        const favoritesData = await getAllFavoriteRecipes(sessionToken);
        console.log('Data from getAllFavoriteRecipes:', JSON.stringify(favoritesData));
        if (Array.isArray(favoritesData)) {
          setFavoriteRecipes(favoritesData);
        }

        // Fetch dislikes
        const dislikesData = await getAllDislikedRecipes(sessionToken);
        if (Array.isArray(dislikesData)) {
          setDislikedRecipes(dislikesData);
        }
      } catch (error) {
        console.error('Error fetching favorites/dislikes:', error);
      }
    };
    
    fetchFavoritesAndDislikes();
  }, [sessionToken, getAllFavoriteRecipes, getAllDislikedRecipes]);

  // Fetch pantry ingredients for pantry match calculation
  useEffect(() => {
    const fetchPantryIngredients = async () => {
      if (!sessionToken || !pgUser?.pantry_id || !pgUser?.id) return;
      
      try {
        const response = await fetchWithCSRF(`/api/v1/pantry-ingredient/pantry/${pgUser.pantry_id}/app_user/${pgUser.id}`, {
          headers: {
            'Authorization': `Bearer ${sessionToken}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data)) {
            setPantryIngredients(data);
          }
        }
      } catch (error) {
        console.error('Error fetching pantry ingredients:', error);
        // Silently fail - pantry match just won't show
      }
    };

    fetchPantryIngredients();
  }, [sessionToken, pgUser?.pantry_id, pgUser?.id, fetchWithCSRF]);


  const breakpointColumns = {
    default: 4,
    1100: 3,
    700: 2,
    500: 2
  };



  // One-time auth initialization
  useEffect(() => {
    const initializeAuth = () => {
      if (pgUser === null) {
        setIsAuthLoading(false);
        return;
      }
      setIsAuthLoading(false);
    };
    initializeAuth();
  }, []); // Empty dependency array - only run once on mount

  useEffect(() => {
    if (!pgUser) return; // Don't fetch if we're not authenticated

    const fetchData = async () => {
      if (!sessionToken) return; // Don't fetch if we don't have a token yet
      
      setIsLoading(true);
      setError(null);

      try {
        // Fetch all available cuisines and recipes in parallel
        const [cuisineResponse, recipeResponse] = await Promise.all([
          fetchWithCSRF('/api/v1/cuisine', {
            headers: {
              'Authorization': `Bearer ${sessionToken}`
            }
          }),
          fetchWithCSRF('/api/v1/recipe/with-ingredients', {
            headers: {
              'Authorization': `Bearer ${sessionToken}`
            }
          })
        ]);

        if (!cuisineResponse.ok) throw new Error('Failed to fetch cuisines');
        if (!recipeResponse.ok) throw new Error('Failed to fetch recipes');

        const cuisineData = await cuisineResponse.json();
        console.log("cuisineData: ", cuisineData);
        const recipeData = await recipeResponse.json();
        console.log("recipeData: ", recipeData);

        setCuisines(cuisineData);
        setRecipes(recipeData);
      } catch (error) {
        console.error('Error fetching initial data:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [sessionToken]); // Only re-run when sessionToken changes

  const [isFilterLoading, setIsFilterLoading] = useState(false);

  // Effect for handling cuisine selection changes
  useEffect(() => {
    if (!sessionToken) return;

    const fetchRecipesByCuisines = async () => {
      setIsFetching(true);
      
      try {
        if (selectedCuisines.length === 0) {
          // Fetch all recipes if no cuisines selected
          const response = await fetch('/api/v1/recipe/with-ingredients', {
            headers: {
              'Authorization': `Bearer ${sessionToken}`
            }
          });
          
          if (!response.ok) {
            throw new Error('Error fetching all recipes');
          }
          
          const data = await response.json();
          setRecipes(data);
          return;
        }

        // First fetch recipes by cuisine IDs
        const cuisineIds = selectedCuisines.join(',');
        const cuisineResponse = await fetch(`/api/v1/recipe-cuisine/multiple_cuisine_ids/${cuisineIds}`, {
          headers: {
            'Authorization': `Bearer ${sessionToken}`
          }
        });
        
        if (!cuisineResponse.ok) {
          throw new Error('Error fetching recipes by cuisines');
        }
        
        const cuisineData = await cuisineResponse.json();
        const recipeIds = [...new Set(cuisineData.map(item => item.recipe_id))];

        if (recipeIds.length === 0) {
          setRecipes([]);
          return;
        }

        // Then fetch the actual recipes using the IDs
        const recipesResponse = await fetchWithCSRF('/api/v1/recipe/multiple_ids', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionToken}`
          },
          body: JSON.stringify({ ids: recipeIds }),
        });
        console.log("recipesResponse: ", recipesResponse);
        if (!recipesResponse.ok) {
          throw new Error('Error fetching recipe details');
        }

        const recipeData = await recipesResponse.json();
        setRecipes(recipeData);
      } catch (error) {
        console.error('Error in fetchRecipesByCuisines:', error);
        setRecipes([]);
      } finally {
        setIsFetching(false);
      }
    };

    fetchRecipesByCuisines();
  }, [selectedCuisines, sessionToken]);



  useEffect(() => {
    // Force scroll to start
    if (filtersRef.current) {
      filtersRef.current.scrollLeft = 0;
    }
  }, [cuisines]);  // Run when cuisines are loaded

  // Log the recipes state whenever it changes
  // useEffect(() => {
  //   alert('Recipes state updated:', recipes);
  // }, [recipes]);

  const handleCuisineChange = (cuisineId) => {
    setSelectedCuisines(prevSelected => 
      prevSelected.includes(cuisineId)
        ? prevSelected.filter(id => id !== cuisineId)
        : [...prevSelected, cuisineId]
    );
  };

  const filteredRecipes = recipes.filter(recipe => {
    const searchLower = searchTerm.toLowerCase();
    
    // Search by recipe name
    if (recipe.name.toLowerCase().includes(searchLower)) {
      return true;
    }
    
    // Search by ingredient names
    if (recipe.ingredients && Array.isArray(recipe.ingredients)) {
      return recipe.ingredients.some(ingredient => 
        ingredient.ingredient_name && 
        ingredient.ingredient_name.toLowerCase().includes(searchLower)
      );
    }
    
    return false;
  });

  return (
    <ErrorBoundary fallbackMessage="Unable to load recipes. Please try again later.">
      <div className="recipe-list container">
        {isAuthLoading ? (
          <div className="text-center my-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : !pgUser ? (
          <div className="alert alert-warning">
            <p className="h5 mb-3">Unable to connect to the server. Please try again later or send a message to <a href="mailto:support@getautochef.com">support@getautochef.com</a></p>
            <Button onClick={() => window.location.reload()}>Retry</Button>
          </div>
        ) : isLoading ? (
          <div className="text-center my-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading recipes...</span>
            </Spinner>
          </div>
        ) : error ? (
          <div className="alert alert-danger">
            <p className="h5 mb-3">Error loading recipes. Please try again later.</p>
            <Button onClick={() => window.location.reload()} variant="danger">Retry</Button>
          </div>
        ) : filteredRecipes.length === 0 && searchTerm ? (
          <div className="text-center my-5">No recipes found matching "{searchTerm}"</div>
        ) : (
          <>
            <div className="cuisine-filters-container">
              <div className="cuisine-filters-fade-left"></div>
              {/* <div className="cuisine-filters" ref={filtersRef}>
                {cuisines.map(cuisine => (
                  <button
                    key={cuisine.id}
                    className={`btn ${
                      selectedCuisines.includes(cuisine.id) 
                        ? 'btn-primary active' 
                        : 'btn-outline-primary'
                    } mb-2 cuisine-filter-option`}
                    onClick={() => handleCuisineChange(cuisine.id)}
                    type="button"
                  >
                    {cuisine.name}
                  </button>
                ))}
              </div> */}
              <div className="cuisine-filters-fade-right"></div>
            </div>

            <div className={`recipe-grid-container ${isFetching ? 'fetching' : ''}`}>
              <Masonry
                breakpointCols={breakpointColumns}
                className="my-masonry-grid"
                columnClassName="my-masonry-grid_column"
                style={{ marginLeft: '-10px' }}
              >
                {filteredRecipes.map(recipe => (
                                  <RecipeCard 
                                    key={recipe.id} 
                                    recipe={recipe} 
                                    style={{ marginBottom: '10px' }}
                                    isFavorite={favoriteRecipes.includes(Number(recipe.id))}
                                    isDisliked={dislikedRecipes.includes(Number(recipe.id))}
                                    pantryIngredients={pantryIngredients}
                                    onMealAdded={onMealAdded}
                                  />
                                ))}
              </Masonry>
              {isFetching && (
                <div className="fetching-overlay">
                  <div className="fetching-indicator" />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </ErrorBoundary>
  );
}

export default RecipeList;
